import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config';
import { 
    Send, ArrowLeft, MapPin, Star, Tag, 
    Phone, Mail, Instagram, Facebook 
} from 'lucide-react';
import CheckAvailabilityModal from '../components/CheckAvailabilityModal';
import PublicAvailabilityCalendar from '../components/PublicAvailabilityCalendar';
import './VendorDetail.css';

interface MarketVendor {
    _id: string;
    name: string;
    category: string;
    contactNumber: string;
    email?: string;
    city?: string;
    description?: string;
    priceRange: string;
    rating: number;
    images?: string[];
    instagramUrl?: string;
    facebookUrl?: string;
    packages?: Array<{
        name: string;
        price: number;
        priceUnit?: string;
        description?: string;
        features?: string[];
    }>;
}

const SHOPPING_CATEGORIES = ['Clothing', 'Jewelry', 'Gifts & Favors'];

const VendorDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user, accessToken } = useAuth();
    const history = useHistory();
    const [vendor, setVendor] = useState<MarketVendor | null>(null);
    const [loading, setLoading] = useState(true);
    const [isShortlisted, setIsShortlisted] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
        const [selectedPackage, setSelectedPackage] = useState<{ name: string; price: number; priceUnit?: string } | null>(null);
    
    // Selection state for auto-filling the modal
    const [selectedFromDate, setSelectedFromDate] = useState('');
    const [selectedToDate, setSelectedToDate] = useState('');

    useEffect(() => {
        const fetchVendorData = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/market-vendors/${id}`);
                const data = await res.json();
                
                if (res.ok) {
                    setVendor(data.vendor);
                    if (data.vendor.packages && data.vendor.packages.length > 0) {
                        setSelectedPackage({
                            name: data.vendor.packages[0].name,
                            price: data.vendor.packages[0].price,
                            priceUnit: data.vendor.packages[0].priceUnit
                        });
                    }
                } else {
                    alert('Vendor not found');
                    history.push('/marketplace');
                    return;
                }

                if (accessToken && user && (user.role === 'admin' || user.role === 'agency')) {
                    const shortlistRes = await fetch(`${API_BASE_URL}/api/market-vendors/shortlist`, {
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    });
                    if (shortlistRes.ok) {
                        const shortlistData = await shortlistRes.json();
                        setIsShortlisted(shortlistData.shortlists.some((s: any) => s.marketVendorId?._id === id));
                    }
                }
            } catch (err) {
                console.error('Error fetching vendor:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchVendorData();
    }, [id, accessToken, user, history]);

    const handleShortlist = async () => {
        if (!user || (user.role !== 'admin' && user.role !== 'agency')) {
            alert('Please login as a Wedding Manager or Agency to shortlist vendors.');
            history.push('/login');
            return;
        }

        setActionLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/${id}/shortlist`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    packageName: selectedPackage?.name,
                    packagePrice: selectedPackage?.price,
                    packagePriceUnit: selectedPackage?.priceUnit
                })
            });
            if (res.ok) {
                setIsShortlisted(true);
            } else {
                const err = await res.json();
                alert(err.message || 'Failed to shortlist vendor');
            }
        } catch (error) {
            console.error('Error shortlisting:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleInquirySubmit = async (data: any) => {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_BASE_URL}/api/market-vendors/${id}/enquiry`, {
            method: 'POST',
            headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error('Failed to send enquiry');
    };

    const handleDateSelect = (from: string, to: string) => {
        setSelectedFromDate(from);
        setSelectedToDate(to);
        if (from) {
            setIsModalOpen(true);
        }
    };

    if (loading) return <div className="loading" style={{ padding: '4rem', textAlign: 'center' }}>Loading Vendor Details...</div>;
    if (!vendor) return null;

    return (
        <div className="vendor-detail-page">
            <div className="vendor-hero" style={{ 
                backgroundImage: `url(${vendor.images && vendor.images.length > 0 ? vendor.images[0] : 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=2070'})` 
            }}>
                <div className="hero-overlay-premium"></div>
                <div className="hero-content container">
                    <button className="back-btn-premium" onClick={() => history.goBack()}>
                        <ArrowLeft size={18} /> Back to Marketplace
                    </button>
                    <div className="hero-info-animate">
                        <div className="vendor-badge-premium">{vendor.category}</div>
                        <h1 className="vendor-name-title-premium">{vendor.name}</h1>
                        <div className="vendor-quick-stats-premium">
                            <span className="stat-item"><MapPin size={18} /> {vendor.city || 'Available Nationwide'}</span>
                            <span className="stat-item"><Star size={18} fill="#C5A059" /> {(vendor.rating ?? 4.5).toFixed(1)} Rating</span>
                            <span className="stat-item"><Tag size={18} /> {vendor.priceRange}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container vendor-info-section">
                <div className="vendor-main-content">
                    <section className="about-vendor">
                        <h2>About {vendor.name}</h2>
                        <p>{vendor.description || "This premium vendor brings exceptional quality to your wedding, ensuring your special day is unforgettable."}</p>
                        
                        <div className="contact-card-premium">
                            <h3>Contact Information</h3>
                            <div className="contact-details-grid">
                                <div className="contact-item-premium">
                                    <Phone size={20} />
                                    <div>
                                        <label>Call/WhatsApp</label>
                                        <p>{vendor.contactNumber}</p>
                                    </div>
                                </div>
                                {vendor.email && (
                                    <div className="contact-item-premium">
                                        <Mail size={20} />
                                        <div>
                                            <label>Email Address</label>
                                            <p>{vendor.email}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="social-links-premium">
                                {vendor.instagramUrl && (
                                    <a href={vendor.instagramUrl} target="_blank" rel="noreferrer" className="social-btn-premium insta">
                                        <Instagram size={18} /> Instagram
                                    </a>
                                )}
                                {vendor.facebookUrl && (
                                    <a href={vendor.facebookUrl} target="_blank" rel="noreferrer" className="social-btn-premium fb">
                                        <Facebook size={18} /> Facebook
                                    </a>
                                )}
                            </div>
                        </div>
                    </section>

                    {vendor.packages && vendor.packages.length > 0 && (
                        <section className="vendor-packages" style={{ marginTop: '2.5rem', marginBottom: '2.5rem' }}>
                            <h2 style={{ color: '#d4af37', marginBottom: '1.5rem' }}>Available Packages & Plans</h2>
                            <div className="packages-premium-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
                                {vendor.packages.map((pkg, idx) => {
                                    const isSelected = selectedPackage?.name === pkg.name;
                                    return (
                                        <div 
                                            key={idx} 
                                            className={`pkg-premium-card ${isSelected ? 'selected' : ''}`}
                                            onClick={() => setSelectedPackage({ name: pkg.name, price: pkg.price, priceUnit: pkg.priceUnit })}
                                            style={{
                                                border: isSelected ? '2px solid #d4af37' : '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '12px',
                                                padding: '20px',
                                                background: isSelected ? 'rgba(212,175,55,0.08)' : 'rgba(255,255,255,0.03)',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                boxShadow: isSelected ? '0 8px 24px rgba(212,175,55,0.15)' : 'none',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {isSelected && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    background: '#d4af37',
                                                    color: '#000',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 'bold',
                                                    padding: '4px 12px',
                                                    borderBottomLeftRadius: '8px',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    Selected
                                                </div>
                                            )}
                                            <div>
                                                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.25rem', color: isSelected ? '#d4af37' : '#fff' }}>{pkg.name}</h3>
                                                <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d4af37', marginBottom: '15px' }}>
                                                    ₹{pkg.price.toLocaleString('en-IN')}{pkg.priceUnit ? ` / ${pkg.priceUnit}` : ''}
                                                </div>
                                                {pkg.description && (
                                                    <p style={{ fontSize: '0.9rem', color: '#ccc', lineHeight: '1.5', marginBottom: '15px' }}>
                                                        {pkg.description}
                                                    </p>
                                                )}
                                            </div>
                                            {pkg.features && pkg.features.length > 0 && (
                                                <ul style={{ listStyle: 'none', padding: 0, margin: '15px 0 0 0', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '15px' }}>
                                                    {pkg.features.map((f, fi) => (
                                                        <li key={fi} style={{ fontSize: '0.85rem', color: '#aaa', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                            <span style={{ color: '#d4af37', fontWeight: 'bold' }}>✓</span> {f}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {vendor.images && vendor.images.length > 1 && (
                        <section className="vendor-gallery">
                            <h2>Gallery</h2>
                            <div className="gallery-grid">
                                {vendor.images.slice(1).map((img, idx) => (
                                    <img key={idx} src={img} alt={`${vendor.name} gallery ${idx}`} className="gallery-image" />
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <div className="vendor-sidebar">
                    <div className="action-card sticky-card">
                        <h3>{vendor.category && SHOPPING_CATEGORIES.includes(vendor.category) ? 'Shop this Boutique' : 'Secure your Date'}</h3>
                        <p>
                            {vendor.category && SHOPPING_CATEGORIES.includes(vendor.category) 
                                ? 'Add premium products from this boutique directly to your cart.' 
                                : 'Select dates on the calendar below to check availability and enquire.'}
                        </p>
                        
                        <div className="action-buttons-stack" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem' }}>
                            {vendor.category && SHOPPING_CATEGORIES.includes(vendor.category) ? (
                                <button 
                                    className="primary-cta-btn book-cart-btn"
                                    onClick={() => alert('Added to Cart successfully!')}
                                    style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #FF4081, #2196F3)', color: '#fff', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                >
                                    🛍️ Add to Cart
                                </button>
                            ) : (
                                <div className="embedded-availability-section">
                                    <PublicAvailabilityCalendar 
                                        vendorId={vendor._id}
                                        selectedFromDate={selectedFromDate}
                                        selectedToDate={selectedToDate}
                                        onDateSelect={handleDateSelect}
                                    />
                                    <button 
                                        className="primary-cta-btn"
                                        onClick={() => setIsModalOpen(true)}
                                        style={{ width: '100%', padding: '1rem', borderRadius: '12px', border: 'none', background: '#1a1a1a', color: '#d4af37', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '1rem' }}
                                    >
                                        <Send size={18} /> Send Booking Enquiry
                                    </button>
                                </div>
                            )}

                            <button 
                                className={`secondary-cta-btn ${isShortlisted ? 'added' : ''}`}
                                onClick={handleShortlist}
                                disabled={isShortlisted || actionLoading}
                                style={{ width: '100%', padding: '1rem', borderRadius: '8px', border: isShortlisted ? '1px solid #E2E8F0' : '1px solid #FF4081', background: isShortlisted ? '#F8F9FE' : '#ffffff', color: isShortlisted ? '#A0AEC0' : '#FF4081', fontWeight: 'bold', fontSize: '1rem', cursor: isShortlisted ? 'not-allowed' : 'pointer' }}
                            >
                                {actionLoading ? 'Adding...' : isShortlisted ? '✓ In Your Shortlist' : '🤍 Add to Shortlist'}
                            </button>
                        </div>
                        
                        {!user && (
                            <p className="login-prompt" style={{ fontSize: '0.85rem', color: '#718096', textAlign: 'center', marginTop: '1rem' }}>You must be logged in to book or shortlist.</p>
                        )}
                    </div>
                </div>
            </div>
            <CheckAvailabilityModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                vendorNames={[vendor.name]}
                vendorIds={[vendor._id]}
                onSubmit={handleInquirySubmit}
            />
        </div>
    );
};

export default VendorDetail;
